import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { generalAdviceAPI, ParentType } from "@/lib/general-advice-api";
import { queryKeys, invalidateQueries } from "@/lib/query-keys";
import type {
  CreateGeneralAdviceRequest,
  UpdateGeneralAdviceRequest,
} from "@/types";

export const useGeneralAdvice = (
  appointmentId: string,
  consolidated: boolean = false,
  parentType: ParentType = "prescriptions"
) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.consultation.patientData.generalAdvice(
      appointmentId,
      consolidated,
      parentType
    ),
    queryFn: () =>
      generalAdviceAPI.get(appointmentId, consolidated, parentType),
    enabled: !!appointmentId,
  });

  const create = useMutation({
    mutationFn: (data: CreateGeneralAdviceRequest) =>
      generalAdviceAPI.create(appointmentId, data, parentType),
    // onSettled: a timed-out or failed save may still have reached the server
    onSettled: () => {
      queryClient.invalidateQueries(
        invalidateQueries.patientGeneralAdvice(
          appointmentId,
          consolidated,
          parentType
        )
      );
    },
  });

  const update = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateGeneralAdviceRequest;
    }) => generalAdviceAPI.update(id, data, parentType),
    // onSettled: a timed-out or failed save may still have reached the server
    onSettled: () => {
      queryClient.invalidateQueries(
        invalidateQueries.patientGeneralAdvice(
          appointmentId,
          consolidated,
          parentType
        )
      );
    },
  });

  return {
    ...query,
    create,
    update,
  };
};
